import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  orderBy,
} from "firebase/firestore";
import LanguageSwitcher from "./LanguageSwitcher";

function DashboardLayout({ children }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [userRole, setUserRole] = useState(null);
  const [userEntityId, setUserEntityId] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [candidates, setCandidates] = useState([]);

  // Fetch user role and entityId
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || null);
            setUserEntityId(userData.entityId || null);
          } else {
            // User document doesn't exist - this shouldn't happen but handle gracefully
            console.warn(
              "User document not found in Firestore for uid:",
              user.uid
            );
            setUserRole(null);
            setUserEntityId(null);
          }
        } catch (error) {
          console.error("Error fetching user role:", error);
          // If permission error, user might not have document yet
          if (
            error.code === "permission-denied" ||
            error.code === "missing-or-insufficient-permissions"
          ) {
            console.error(
              "Permission denied - user document may not exist. Please create admin via /setup-admin"
            );
          }
          setUserRole(null);
          setUserEntityId(null);
        }
      } else {
        setUserRole(null);
        setUserEntityId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Fetch lessons and candidates for filtering
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [lessonsSnapshot, candidatesSnapshot] = await Promise.all([
          getDocs(collection(db, "lessons")),
          getDocs(collection(db, "candidates")),
        ]);
        setLessons(
          lessonsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        );
        setCandidates(
          candidatesSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
        );
      } catch (error) {
        console.error("Error fetching data for notifications:", error);
      }
    };
    fetchData();
  }, []);

  // Fetch unread notifications count - filtered by user
  useEffect(() => {
    if (!userRole) return;

    const q = query(
      collection(db, "notifications"),
      where("read", "==", false)
    );

    // Use onSnapshot for real-time updates
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const allNotifications = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter notifications based on user role
        let filteredNotifications = allNotifications;

        // For candidates, only show notifications related to them
        if (userRole === "candidate" && userEntityId) {
          filteredNotifications = allNotifications.filter((notification) => {
            if (notification.candidateId === userEntityId) return true;
            if (
              notification.entityType === "candidate" &&
              notification.entityId === userEntityId
            )
              return true;
            if (notification.entityType === "lesson") {
              const lesson = lessons.find((l) => l.id === notification.entityId);
              if (lesson && lesson.candidateId === userEntityId) return true;
              if (notification.candidateId === userEntityId) return true;
            }
            if (notification.entityType === "lecture") return true;
            if (notification.recipientId === auth.currentUser?.uid) return true;
            return false;
          });
        }

        // For instructors, only show notifications related to their candidates or themselves
        if (userRole === "instructor" && userEntityId) {
          filteredNotifications = allNotifications.filter((notification) => {
            if (notification.entityType === "lesson") {
              const lesson = lessons.find((l) => l.id === notification.entityId);
              if (lesson) {
                const candidate = candidates.find(
                  (c) => c.id === lesson.candidateId
                );
                if (
                  candidate &&
                  candidate.assignedInstructorId === userEntityId
                )
                  return true;
              }
              if (notification.candidateId) {
                const candidate = candidates.find(
                  (c) => c.id === notification.candidateId
                );
                if (
                  candidate &&
                  candidate.assignedInstructorId === userEntityId
                )
                  return true;
              }
            }
            if (notification.candidateId) {
              const candidate = candidates.find(
                (c) => c.id === notification.candidateId
              );
              if (candidate && candidate.assignedInstructorId === userEntityId)
                return true;
            }
            if (
              notification.entityType === "instructor" &&
              notification.entityId === userEntityId
            )
              return true;
            if (notification.recipientId === auth.currentUser?.uid) return true;
            if (notification.entityType === "lecture") return true;
            return false;
          });
        }

        // For admins, show all notifications
        // (filteredNotifications already equals allNotifications)

        setUnreadNotificationsCount(filteredNotifications.length);
      },
      (error) => {
        console.error("Error fetching unread notifications count:", error);
      }
    );

    return () => unsubscribe();
  }, [userRole, userEntityId, lessons, candidates]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const allNavigation = [
    {
      name: t("sidebar.overview"),
      path: "/dashboard/overview",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      ),
      roles: ["admin"],
    },
    {
      name: t("sidebar.instructors"),
      path: "/dashboard/instructors",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
      roles: ["admin", "candidate"],
    },
    {
      name: t("sidebar.candidates"),
      path: "/dashboard/candidates",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ),
      roles: ["admin", "instructor"],
    },
    {
      name: t("sidebar.lessons"),
      path: "/dashboard/lessons",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
      roles: ["admin", "instructor", "candidate"],
    },
    {
      name: t("sidebar.vehicles"),
      path: "/dashboard/vehicles",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"
          />
        </svg>
      ),
      roles: ["admin"],
    },
    {
      name: t("sidebar.lectures"),
      path: "/dashboard/lectures",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
      roles: ["admin", "instructor", "candidate"],
    },
    {
      name: t("sidebar.videoLectures"),
      path: "/dashboard/video-lectures",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      ),
      roles: ["admin", "instructor", "candidate"],
    },
    {
      name: t("sidebar.payments"),
      path: "/dashboard/payments",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        </svg>
      ),
      roles: ["admin"],
    },
    {
      name: t("sidebar.reports"),
      path: "/dashboard/reports",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      roles: ["admin", "instructor"],
    },
  ];

  // Filter navigation based on user role
  const navigation = allNavigation.filter((item) => {
    if (!userRole) return false; // Don't show navigation until role is loaded
    return item.roles.includes(userRole);
  });

  const allBottomNavigation = [
    {
      name: t("sidebar.notifications"),
      path: "/dashboard/notifications",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
      ),
      badge: unreadNotificationsCount > 0 ? unreadNotificationsCount : null,
      roles: ["admin", "instructor", "candidate"],
    },
    {
      name: t("sidebar.profile"),
      path: "/dashboard/profile",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
      roles: ["admin", "instructor", "candidate"],
    },
  ];

  // Filter bottom navigation based on user role
  const bottomNavigation = allBottomNavigation.filter((item) => {
    if (!userRole) return false; // Don't show navigation until role is loaded
    return item.roles.includes(userRole);
  });

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile menu overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full bg-black transform transition-all duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } ${sidebarCollapsed ? "lg:w-20" : "lg:w-64"} w-64`}
      >
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className="flex items-center justify-between py-4 px-4 border-b border-white/10">
            <div
              className={`flex items-center ${
                sidebarCollapsed ? "lg:justify-center lg:w-full" : "pl-3"
              }`}
            >
              <img
                src={sidebarCollapsed ? "/iconWhite.png" : "/logoWhite.png"}
                alt="Logo"
                className={`${
                  sidebarCollapsed ? "lg:h-8 lg:w-8" : "h-12 w-auto"
                } object-contain`}
                onError={(e) => {
                  e.target.style.display = "none";
                  const svg = document.createElementNS(
                    "http://www.w3.org/2000/svg",
                    "svg"
                  );
                  svg.setAttribute(
                    "class",
                    `${
                      sidebarCollapsed ? "lg:h-8 lg:w-8" : "h-12 w-auto"
                    } text-white`
                  );
                  svg.setAttribute("fill", "none");
                  svg.setAttribute("stroke", "currentColor");
                  svg.setAttribute("viewBox", "0 0 24 24");
                  svg.innerHTML =
                    '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M13 10V3L4 14h7v7l9-11h-7z"></path>';
                  e.target.parentElement.appendChild(svg);
                }}
              />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-400 hover:text-white transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              // Compute name dynamically for instructors based on role
              const displayName =
                item.path === "/dashboard/instructors" &&
                userRole === "candidate"
                  ? t("sidebar.instructor")
                  : item.name;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  title={sidebarCollapsed ? displayName : ""}
                  className={`w-full flex items-center ${
                    sidebarCollapsed ? "lg:justify-center" : "space-x-3"
                  } px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive(item.path)
                      ? "bg-white text-black"
                      : "text-gray-300 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {item.icon}
                  {!sidebarCollapsed && (
                    <span className="font-medium text-sm lg:block">
                      {displayName}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Bottom Navigation */}
          <div className="border-t border-white/10 px-4 py-4 space-y-1">
            {bottomNavigation.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                title={sidebarCollapsed ? item.name : ""}
                className={`w-full flex items-center ${
                  sidebarCollapsed ? "lg:justify-center" : "justify-between"
                } px-4 py-3 rounded-lg transition-all duration-200 relative ${
                  isActive(item.path)
                    ? "bg-white text-black"
                    : "text-gray-300 hover:bg-white/10 hover:text-white"
                }`}
              >
                <div
                  className={`flex items-center ${
                    sidebarCollapsed ? "lg:justify-center" : "space-x-3"
                  }`}
                >
                  {item.icon}
                  {!sidebarCollapsed && (
                    <span className="font-medium text-sm lg:block">
                      {item.name}
                    </span>
                  )}
                </div>
                {item.badge && !sidebarCollapsed && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full lg:block">
                    {item.badge}
                  </span>
                )}
                {item.badge && sidebarCollapsed && (
                  <span className="hidden lg:block absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>
            ))}
          </div>

          {/* Logout Button */}
          <div className="border-t border-white/10 p-4">
            <button
              onClick={handleLogout}
              title={sidebarCollapsed ? t("sidebar.logout") : ""}
              className={`w-full flex items-center ${
                sidebarCollapsed ? "lg:justify-center" : "space-x-3"
              } px-4 py-3 text-gray-300 hover:bg-red-500/10 hover:text-red-400 rounded-lg transition-all duration-200`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              {!sidebarCollapsed && (
                <span className="font-medium text-sm lg:block">
                  {t("sidebar.logout")}
                </span>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`min-h-screen flex flex-col transition-all duration-300 ${
          sidebarCollapsed ? "lg:pl-20" : "lg:pl-64"
        }`}
      >
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 py-4">
            {/* Left section */}
            <div className="flex items-center space-x-4">
              {/* Mobile menu button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-700 hover:text-black transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>

              {/* Desktop sidebar toggle button */}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="hidden lg:block text-gray-700 hover:text-black transition-colors"
                title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {sidebarCollapsed ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 5l7 7-7 7M5 5l7 7-7 7"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                    />
                  )}
                </svg>
              </button>
            </div>

            {/* Page Title - Desktop */}
            <h1 className="text-xl font-bold text-black hidden lg:block flex-1 text-center">
              {(() => {
                const activeNavItem = navigation.find((item) =>
                  isActive(item.path)
                );
                const activeBottomNavItem = bottomNavigation.find((item) =>
                  isActive(item.path)
                );
                if (activeNavItem) {
                  return activeNavItem.path === "/dashboard/instructors" &&
                    userRole === "candidate"
                    ? t("sidebar.instructor")
                    : activeNavItem.name;
                }
                if (activeBottomNavItem) {
                  return activeBottomNavItem.name;
                }
                return t("sidebar.dashboard");
              })()}
            </h1>

            {/* Right Section */}
            <div className="flex items-center space-x-4">
              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* User Menu */}
              <button className="flex items-center space-x-2 text-gray-700 hover:text-black transition-colors">
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-gray-600">A</span>
                </div>
                <span className="hidden sm:block text-sm font-medium">
                  Admin
                </span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>

        {/* Footer */}
        <footer className="border-t border-gray-200 bg-white">
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
              <p className="text-sm text-gray-600">
                © {new Date().getFullYear()} Auto Shkolla. All rights reserved.
              </p>
              <p className="text-sm text-gray-600">
                Developed by{" "}
                <a
                  href="https://www.ardbraha.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black font-medium hover:underline"
                >
                  Ard Braha
                </a>
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default DashboardLayout;
